// bookings/components/tabs/accessories/AccessoryComparisonTable.js
import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import { useFormat } from "../../../../../components/FormatContext";
import { fromCents } from "../../../../../pages/bookings/utils/money";
import "../../../../../assets/styles/BookingForm/AccessoryComparisonTable.css";

const AccessoryComparisonTable = ({
    customerCurrencyCode = "EUR",
    currentAccessories,
    accessories,
    setShowAccessoriesModal,
    mode = "interactive",
    title,
    emptyText = "No extras",

    // ✅ 原来的
    totalCents,

    // ✅ 新增：对比模式
    comparisonRows, // [{ key,name,description,changeType,quantityOriginal,quantityCurrent,priceOriginalCents,priceCurrentCents,deltaCents,isPersonalized }]
    originalTotalCents,
    currentTotalCents,
}) => {
    const { formatCurrency } = useFormat();
    const currency = customerCurrencyCode || "EUR";

    const isInteractive =
        mode === "interactive" && typeof setShowAccessoriesModal === "function";

    const handleOpen = () => {
        if (isInteractive) setShowAccessoriesModal(true);
    };

    const format = (cents) => formatCurrency(fromCents(cents || 0), currency);

    // ✅ 普通列表模式数据（保持你原逻辑）
    const sourceAccessories = accessories ?? currentAccessories ?? [];
    const processedAccessories = useMemo(() => {
        return (sourceAccessories || []).map((acc) => ({
            ...acc,
            status: acc.shopMotorcycleExtraId === null ? "personalized" : "normal",
            size: acc.meta?.size ?? "",
        }));
    }, [sourceAccessories]);

    // ✅ 顶部 Total：普通模式（你原来的）
    const formattedTotal =
        typeof totalCents === "number" ? format(totalCents) : null;

    // ✅ 顶部 Total：对比模式（更友好）
    const diffTotalCents =
        typeof originalTotalCents === "number" && typeof currentTotalCents === "number"
            ? currentTotalCents - originalTotalCents
            : null;

    const isDiffMode = Array.isArray(comparisonRows);

    const renderChangeBadge = (changeType) => {
        if (changeType === "added") return <span className="badge badge-soft ms-2">Added</span>;
        if (changeType === "removed") return <span className="badge badge-soft ms-2">Removed</span>;
        if (changeType === "modified") return <span className="badge badge-soft ms-2">Changed</span>;
        return null;
    };

    return (
        <div className="accessory-table-wrap">
            {title && (
                <div className="accessory-table-title d-flex justify-content-between align-items-center">
                    <span>{title}</span>

                    {/* ✅ Diff 模式：显示 old → new + Δ */}
                    {isDiffMode && typeof originalTotalCents === "number" && typeof currentTotalCents === "number" ? (
                        <div className="text-end">
                            <div className="accessory-total">
                                {format(originalTotalCents)} → {format(currentTotalCents)}
                            </div>
                            <div className="accessory-diff">
                                Δ {format(diffTotalCents)}
                            </div>
                        </div>
                    ) : (
                        /* ✅ 普通模式：保持你原来的 total */
                        formattedTotal && <span className="accessory-total">{formattedTotal}</span>
                    )}
                </div>
            )}

            <Table
                striped={false}
                bordered={false}
                hover={isInteractive}
                responsive
                className={`accessory-table mb-0 ${isInteractive ? "is-interactive" : ""}`}
                onClick={isInteractive ? handleOpen : undefined}
                role={isInteractive ? "button" : undefined}
                aria-label={isInteractive ? "Open accessories details" : "Accessories list"}
                style={isInteractive ? { cursor: "pointer" } : undefined}
            >
                <thead>
                    <tr>
                        <th className="col-name">Name & Description</th>
                        <th className="col-qty text-center">Qty & PVP</th>
                    </tr>
                </thead>

                <tbody>
                    {/* =======================
              ✅ 1) Diff 模式渲染
             ======================= */}
                    {isDiffMode ? (
                        comparisonRows.length > 0 ? (
                            comparisonRows.map((row) => {
                                const isPersonalized = !!row.isPersonalized;
                                const isChanged = row.changeType && row.changeType !== "unchanged";

                                return (
                                    <tr
                                        key={row.key}
                                        className={[
                                            isPersonalized ? "row-personalized" : "",
                                            isChanged ? "row-changed" : "",
                                        ].join(" ")}
                                        onClick={isInteractive ? handleOpen : undefined}
                                    >
                                        <td>
                                            <div className="min-w-0">
                                                <div className="name-line">
                                                    <span className="name-text">{row.name}</span>

                                                    {isPersonalized && (
                                                        <span className="badge badge-soft ms-2">Personalized</span>
                                                    )}

                                                    {renderChangeBadge(row.changeType)}
                                                </div>

                                                {row.description ? (
                                                    <div className="desc-text text-truncate" title={row.description}>
                                                        {row.description}
                                                    </div>
                                                ) : (
                                                    <div className="desc-text text-muted">—</div>
                                                )}
                                            </div>
                                        </td>

                                        <td className="text-center">
                                            <div className="qty-pvp">
                                                {row.changeType === "modified" ? (
                                                    <>
                                                        <div className="qty-text">
                                                            Qty: {row.quantityOriginal} → {row.quantityCurrent}
                                                        </div>
                                                        <div className="pvp-text">
                                                            {format(row.priceOriginalCents)} → {format(row.priceCurrentCents)}
                                                        </div>
                                                        <div className="delta-text">Δ {format(row.deltaCents)}</div>
                                                    </>
                                                ) : row.changeType === "added" ? (
                                                    <>
                                                        <div className="qty-text">Qty: {row.quantityCurrent}</div>
                                                        <div className="pvp-text">{format(row.priceCurrentCents)}</div>
                                                        <div className="delta-text">Δ {format(row.deltaCents)}</div>
                                                    </>
                                                ) : row.changeType === "removed" ? (
                                                    <>
                                                        <div className="qty-text">Qty: {row.quantityOriginal}</div>
                                                        <div className="pvp-text">{format(row.priceOriginalCents)}</div>
                                                        <div className="delta-text">Δ {format(row.deltaCents)}</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="qty-text">Qty: {row.quantityCurrent}</div>
                                                        <div className="pvp-text">{format(row.priceCurrentCents)}</div>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={2} className="text-center text-muted py-4">
                                    {emptyText}
                                </td>
                            </tr>
                        )
                    ) : (
                        /* =======================
                           ✅ 2) 普通列表模式渲染（你原来的）
                           ======================= */
                        processedAccessories.length > 0 ? (
                            processedAccessories.map((accessory, index) => {
                                const nameWithSize = accessory.size
                                    ? `${accessory.name} (${accessory.size})`
                                    : accessory.name;

                                const uniqueKey =
                                    accessory.shopMotorcycleExtraId ||
                                    (accessory.id
                                        ? `${accessory.shopMotorcycleExtraId}-${index}`
                                        : `personalized-${index}-${accessory.name}`);

                                const isPersonalized = accessory.status === "personalized";

                                return (
                                    <tr
                                        key={uniqueKey}
                                        className={isPersonalized ? "row-personalized" : ""}
                                        onClick={isInteractive ? handleOpen : undefined}
                                    >
                                        <td>
                                            <div className="min-w-0">
                                                <div className="name-line">
                                                    <span className="name-text">{nameWithSize}</span>

                                                    {isPersonalized && (
                                                        <span className="badge badge-soft ms-2">Personalized</span>
                                                    )}
                                                </div>

                                                {accessory.description ? (
                                                    <div className="desc-text text-truncate" title={accessory.description}>
                                                        {accessory.description}
                                                    </div>
                                                ) : (
                                                    <div className="desc-text text-muted">—</div>
                                                )}
                                            </div>
                                        </td>

                                        <td className="text-center">
                                            <div className="qty-pvp">
                                                <div className="qty-text">Qty: {accessory.quantity}</div>
                                                <div className="pvp-text">
                                                    {formatCurrency(fromCents(accessory.priceCents || 0), customerCurrencyCode)}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={2} className="text-center text-muted py-4">
                                    {emptyText}
                                </td>
                            </tr>
                        )
                    )}
                </tbody>
            </Table>
        </div>
    );
};

export default AccessoryComparisonTable;